import { useEffect, useCallback } from "react";
import {
  Page,
  Card,
  BlockStack,
  Text,
  Badge,
  Button,
  InlineStack,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CommentsFeature from "components/comment/comments";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { fetchAssetDetails, setDestroy } from "./asset-container.slice";
import AssetViewSkeleton from "./asset-view-skeleton";
import { CommentTypes } from "types/Comment";
import PrettyDate from "components/format/PrettyDate";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import DataTable from "components/datatable/Datatable";
import { PersonAddIcon } from "@shopify/polaris-icons";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";

const AssetsContainer: React.FC = () => {
  const { t } = useTranslation();
  const { companyId, assetId } = useParams<{
    companyId: string;
    assetId: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();

  // Fetch project by ID
  const { assetDetails, isLoading, isError, requestedAssetId } = useSelector(
    (state: RootState) => state.assetContainerReducer
  );

  const loadAssetData = useCallback(() => {
    if (companyId && assetId) {
      dispatch(fetchAssetDetails({ companyId, id: assetId }));
    }
  }, [dispatch, companyId, assetId]);

  useEffect(() => {
    loadAssetData();
    return () => {
      dispatch(setDestroy());
    };
  }, [loadAssetData, dispatch]);

  const handleEditClick = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("AssetsForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  // The loaded asset matches the route only when its id equals assetId — guards
  // against briefly showing the previous asset's data after navigating to a
  // different one (the route reconciles instead of remounting).
  const isAssetReady = !!assetDetails && assetDetails.id === assetId;

  // If loading the requested asset failed, show an error with a retry path
  // instead of an endless skeleton. requestedAssetId scopes the error to the
  // asset that actually failed (the slice clears it when a new fetch starts),
  // so a stale error never blocks a different asset, and a failed background
  // refetch (when valid data is already shown) keeps the page visible.
  if (isError && requestedAssetId === assetId && !isAssetReady) {
    return (
      <Page
        fullWidth
        backAction={{
          content: "assets-container",
          url: `/app/${companyId}/assets`,
        }}
        title={t("assets", { defaultValue: "Assets" })}
      >
        <Card>
          <BlockStack gap="300">
            <Text as="p" tone="critical">
              {t("not_fetch", { defaultValue: "Could not load asset" })}
            </Text>
            <div>
              <Button onClick={loadAssetData}>
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </div>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  if (!isAssetReady) {
    return <AssetViewSkeleton />;
  }

  const assetNameNode = assetDetails.name ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_name")}: </strong> {assetDetails.name}
    </Text>
  ) : (
    <Text as="p">{t("no_name_provided")}</Text>
  );

  const assetTypeNode = assetDetails.type?.title ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_type")}: </strong> {assetDetails.type.title}
    </Text>
  ) : (
    <Text as="p">{t("no_type_provided")}</Text>
  );

  const assetModelNode = assetDetails.model ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_model")}: </strong> {assetDetails.model}
    </Text>
  ) : (
    <Text as="p">{t("no_model_provided")}</Text>
  );

  const assetBrandNode = assetDetails.brand ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_brand")}: </strong> {assetDetails.brand}
    </Text>
  ) : (
    <Text as="p">{t("no_brand_provided")}</Text>
  );

  const assetSerialNode = assetDetails.serial ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_serial")}: </strong> {assetDetails.serial}
    </Text>
  ) : (
    <Text as="p">{t("no_serial_provided")}</Text>
  );

  const assetAssignedToNode = assetDetails.assigned_to ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_assigned_to")}: </strong>{" "}
      {assetDetails?.assigned_to?.name}
    </Text>
  ) : (
    <Text as="p">{t("no_assigned_to_provided")}</Text>
  );

  const assetNotesNode = assetDetails.notes ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_notes")}: </strong> {assetDetails.notes}
    </Text>
  ) : (
    <Text as="p">{t("no_notes_provided")}</Text>
  );

  const assetPurchaseDateNode = assetDetails.purchase_date ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_purchase_date")}: </strong>{" "}
      <PrettyDate dateString={assetDetails.purchase_date} />
    </Text>
  ) : (
    <Text as="p">{t("no_purchase_date_provided")}</Text>
  );

  const assetIsBrokenNode = (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_broken")}: </strong>{" "}
      {assetDetails.isBroken ? "Yes" : "No"}
    </Text>
  );

  const assetStatusNode = assetDetails.status ? (
    <Text as="p" variant="bodyMd">
      <strong>{t("asset_status")}: </strong> {assetDetails.status}
    </Text>
  ) : (
    <Text as="p">{t("no_status_provided")}</Text>
  );

  const assetCreatedAt = (
    <Text as="p" variant="bodyMd">
      <strong>{t("createdAt")}: </strong>{" "}
      <PrettyDate showTime={true} dateString={assetDetails.createdAt} />
    </Text>
  );

  const handleAssignUser = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("UserAssignToAssetsForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleReturnAsset = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("AssetsReturnForm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const handleAssetForceReturn = ({ id }: { id: string }) => {
    dispatch(setModalxComponent("AssetForceReturnConfirm"));
    dispatch(setModalxComponentProps({ id }));
  };

  const assetsHistoryCollection =
    assetDetails.assetHistory.map((item: any, index) => ({
      index,
      id: item.id,
      company: assetDetails.company,
      previousUser: item.previousUser,
      user: item.user,
      assignedDate: item.assignedDate,
      disabled: item.user
        ? !assetDetails.assetHistory[Number(index)]?.files?.find(
            (fileEntity: any) =>
              fileEntity.assetsHistoryFileSubject === "assign"
          )
        : !assetDetails.assetHistory[Number(index) + 1]?.files?.find(
            (fileEntity: any) =>
              fileEntity.assetsHistoryFileSubject === "return"
          ),
      files: item.files,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    })) || [];

  const handleRowClick = ({
    company,
    files,
    index,
  }: {
    index: string;
    company: string;
    files: any;
  }) => {
    let fileEntity = null;
    if (!files.length) {
      // find index+1 files(as return file)
      if (assetDetails.assetHistory.length > Number(index + 1)) {
        const targetAssetHistoryFiles: any =
          assetDetails.assetHistory[Number(index) + 1]?.files;

        fileEntity = targetAssetHistoryFiles.find(
          (file: any) => file.assetsHistoryFileSubject === "return"
        );
      }
    } else {
      fileEntity = files.find(
        (file: any) => file.assetsHistoryFileSubject === "assign"
      );
    }

    const fileId = fileEntity?.id || null;
    const cateId = fileEntity?.templateId?.category || null;

    // Preview the act in place; the modal carries the link into the documents
    // module. Previously the row opened a new tab only when the act's template
    // had a category — without one the click did nothing at all.
    if (company && fileId) {
      dispatch(setModalxComponent("AssetActModal"));
      dispatch(setModalxStyle("large"));
      dispatch(
        setModalxOptions({ title: t("acceptance_transfer_act") })
      );
      dispatch(
        setModalxComponentProps({
          fileId,
          companyId: company,
          categoryId: cateId,
        })
      );
    }
  };

  return (
    <Page
      fullWidth
      backAction={{
        content: "assets-container",
        url: `/app/${companyId}/assets`,
      }}
      title={assetDetails.name}
      titleMetadata={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isLoading && <Spinner size="small" />}
          <AuditLogPopover
            companyId={companyId!}
            subjectType="Asset"
            subjectId={assetId}
            label={t("history", { defaultValue: "History" })}
          />
        </div>
      }
    >
      {/* The data and the issue/return actions come first on a phone and a
          narrow tablet, so nobody scrolls past comments to find them. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <CommentsFeature
                id={assetId}
                CommentTypes={CommentTypes.Assets}
              />
            </Card>
            {assetDetails.assetHistory.length > 0 && (
              <Card>
                <InlineStack align="space-between">
                  <Text variant="headingLg" as="h4">
                    {t("asset_assigned_tracks")}(
                    {assetDetails.assetHistory.length})
                  </Text>
                  <InlineStack align="space-between" gap="500">
                    <Button
                      onClick={() => handleAssignUser({ id: assetDetails.id })}
                      icon={PersonAddIcon as any}
                      accessibilityLabel={t("asset_assign_user")}
                    ></Button>
                    <Button
                      onClick={() =>
                        handleAssetForceReturn({ id: assetDetails.id })
                      }
                      disabled={!assetDetails.assigned_to}
                    >
                      {t("asset_force_return")}
                    </Button>
                  </InlineStack>
                </InlineStack>
                <DataTable
                  collection={assetsHistoryCollection}
                  columns={[
                    { key: "#" },
                    {
                      key: "previousUser.name",
                      name: t("previous_assigned_user"),
                      translationRequired: true,
                      mobileView: "topLeft",
                    },
                    {
                      key: "user.name",
                      name: t("asset_assigned_user"),
                      translationRequired: true,
                      mobileView: "topRight",
                    },
                    {
                      key: "assignedDate",
                      name: t("assigned_date"),
                      type: "date",
                      mobileView: "bottomLeft",
                    },
                    {
                      key: "",
                      key1: "",
                      name: t("acceptance_transfer_act"),
                      type: "toggleValue",
                      content: t("view"),
                      onClick: (item: any) => handleRowClick(item),
                      mobileView: "bottomRight",
                    },
                  ]}
                />
              </Card>
            )}
          </BlockStack>
        </div>
        <div className="order-first min-w-0 lg:order-last">
          <BlockStack gap="500">
            <Card roundedAbove="sm">
              <div className="flex justify-end">
                <Button
                  size="large"
                  onClick={() => handleEditClick({ id: assetId })}
                >
                  {t("edit")}
                </Button>
              </div>
              {assetNameNode}
              {assetTypeNode}
              {assetModelNode}
              {assetBrandNode}
              {assetSerialNode}
              {assetPurchaseDateNode}
              {assetStatusNode}
              {assetAssignedToNode}
              {assetIsBrokenNode}
              {assetNotesNode}
              {assetCreatedAt}
            </Card>

            {/* box 1 */}
            <div className="card-wrapper" style={{ marginTop: "20px" }}>
              <div
                className={`card-box ${
                  assetDetails.fileEntity ? "border-green" : "border-yellow"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("assign_asset")}
                  </Text>
                  <InlineStack align="center">
                    <Button
                      onClick={() => handleAssignUser({ id: assetDetails.id })}
                      disabled={assetDetails?.fileEntity?.isSigned}
                    >
                      {t("select_the_user")}
                    </Button>
                  </InlineStack>
                </Card>
              </div>
              <div className="connector-wrapper">
                <div className="vertical-line"></div>
                <div className="arrow-down"></div>
              </div>
            </div>

            {/* box 2 */}
            <div className="card-wrapper">
              <div
                className={`card-box ${
                  assetDetails?.fileEntity?.isSigned
                    ? "border-green"
                    : !assetDetails?.fileEntity
                    ? "border-grey"
                    : "border-yellow"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("signature_status")}
                  </Text>
                  <InlineStack align="center">
                    {assetDetails?.fileEntity?.signersList?.length > 0 && (
                      <div className="content_break content_margin">
                        {assetDetails?.fileEntity?.signersList?.map(
                          (signer: any) => (
                            <Text variant="bodyMd" as="p" key={signer.id}>
                              {t("sign_status")} : {signer?.user?.name}{" "}
                              {signer.status === "signed" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.updatedAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="success">{t("signed")}</Badge>{" "}
                                </>
                              ) : signer.status === "pending" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.createdAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="attention">{t("pending")}</Badge>
                                </>
                              ) : signer.status === "rejected" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.updatedAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="critical">{t("declined")}</Badge>
                                </>
                              ) : (
                                <></>
                              )}
                            </Text>
                          )
                        )}
                      </div>
                    )}
                  </InlineStack>
                  <InlineStack align="center">
                    {assetDetails?.fileEntity?.isSigned && (
                      <Badge tone="success">{t("signed")}</Badge>
                    )}
                  </InlineStack>
                </Card>
              </div>
              <div className="connector-wrapper">
                <div className="vertical-line"></div>
                <div className="arrow-down"></div>
              </div>
            </div>

            {/* box 3 */}
            <div className="card-wrapper">
              <div
                className={`card-box ${
                  assetDetails?.fileEntity?.isSigned
                    ? "border-green"
                    : "border-grey"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("assignation_info")}
                  </Text>
                  <InlineStack align="center">
                    {assetDetails?.fileEntity?.signersList?.length > 0 && (
                      <div className="content_break content_margin">
                        {assetDetails?.fileEntity?.isSigned ? (
                          assetDetails?.fileEntity?.signersList?.map(
                            (signer: any) => {
                              if (signer.position === "asset assigner") {
                                return <></>;
                              }
                              return (
                                <Text variant="bodyMd" as="p" key={signer.id}>
                                  {t("assign_asset")} : {signer?.user?.name}{" "}
                                  {signer.status === "signed" ? (
                                    <PrettyDate
                                      dateString={signer.updatedAt}
                                      showTime={true}
                                    />
                                  ) : (
                                    <PrettyDate
                                      dateString={assetDetails.createdAt}
                                      showTime={true}
                                    />
                                  )}
                                </Text>
                              );
                            }
                          )
                        ) : (
                          <>{t("not_assigned")}</>
                        )}
                      </div>
                    )}
                  </InlineStack>
                </Card>
              </div>
              <div className="connector-wrapper">
                <div className="vertical-line"></div>
                <div className="arrow-down"></div>
              </div>
            </div>
            {/* box 4 */}
            <div className="card-wrapper" style={{ marginTop: "20px" }}>
              <div
                className={`card-box ${
                  assetDetails?.returnFileEntity
                    ? "border-green"
                    : assetDetails?.fileEntity?.isSigned
                    ? "border-yellow"
                    : "border-grey"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("return_asset")}
                  </Text>
                  <InlineStack align="center">
                    <Button
                      onClick={() => handleReturnAsset({ id: assetDetails.id })}
                      disabled={
                        !assetDetails?.fileEntity?.isSigned ||
                        assetDetails?.returnFileEntity?.isSigned
                      }
                    >
                      {t("return_asset")}
                    </Button>
                  </InlineStack>
                </Card>
              </div>
              <div className="connector-wrapper">
                <div className="vertical-line"></div>
                <div className="arrow-down"></div>
              </div>
            </div>
            {/* box 5 */}
            <div className="card-wrapper">
              <div
                className={`card-box ${
                  assetDetails?.returnFileEntity?.isSigned
                    ? "border-green"
                    : !assetDetails?.returnFileEntity
                    ? "border-grey"
                    : "border-yellow"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("signature_status")}
                  </Text>
                  <InlineStack align="center">
                    {assetDetails?.returnFileEntity?.signersList?.length >
                      0 && (
                      <div className="content_break content_margin">
                        {assetDetails?.returnFileEntity?.signersList?.map(
                          (signer: any) => (
                            <Text variant="bodyMd" as="p" key={signer.id}>
                              {t("sign_status")} : {signer?.user?.name}{" "}
                              {signer.status === "signed" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.updatedAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="success">{t("signed")}</Badge>{" "}
                                </>
                              ) : signer.status === "pending" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.createdAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="attention">{t("pending")}</Badge>
                                </>
                              ) : signer.status === "rejected" ? (
                                <>
                                  <PrettyDate
                                    dateString={signer.updatedAt}
                                    showTime={true}
                                  />{" "}
                                  <Badge tone="critical">{t("declined")}</Badge>
                                </>
                              ) : (
                                <></>
                              )}
                            </Text>
                          )
                        )}
                      </div>
                    )}
                  </InlineStack>
                  <InlineStack align="center">
                    {assetDetails?.returnFileEntity?.isSigned && (
                      <Badge tone="success">{t("signed")}</Badge>
                    )}
                  </InlineStack>
                </Card>
              </div>
              <div className="connector-wrapper">
                <div className="vertical-line"></div>
                <div className="arrow-down"></div>
              </div>
            </div>
            {/* box 6 */}
            <div className="card-wrapper">
              <div
                className={`card-box ${
                  assetDetails?.returnFileEntity?.isSigned
                    ? "border-green"
                    : "border-grey"
                }`}
              >
                <Card roundedAbove="sm">
                  <Text as="h2" variant="headingSm">
                    {t("return_info")}
                  </Text>
                  <InlineStack align="center">
                    {assetDetails?.returnFileEntity?.signersList?.length >
                      0 && (
                      <div className="content_break content_margin">
                        {assetDetails?.returnFileEntity?.isSigned ? (
                          assetDetails?.returnFileEntity?.signersList?.map(
                            (signer: any) => {
                              if (signer.position === "asset assigner") {
                                return <></>;
                              }
                              return (
                                <Text variant="bodyMd" as="p" key={signer.id}>
                                  {t("return_asset")} : {signer?.user?.name}{" "}
                                  {signer.status === "signed" ? (
                                    <PrettyDate
                                      dateString={signer.updatedAt}
                                      showTime={true}
                                    />
                                  ) : (
                                    <PrettyDate
                                      dateString={assetDetails.createdAt}
                                      showTime={true}
                                    />
                                  )}
                                </Text>
                              );
                            }
                          )
                        ) : (
                          <>{t("not_returned")}</>
                        )}
                      </div>
                    )}
                  </InlineStack>
                </Card>
              </div>
            </div>
          </BlockStack>
        </div>
      </div>
    </Page>
  );
};

export default AssetsContainer;
